import { useEffect, useState } from "react";
import axiosInstance from "../../AxiosInstance";
import TelemetryConfigSection from "./TelemetryConfigSection";

/**
 * Hidden cloud diagnostics panel.
 *
 * Fetches `/pdui/<id>/diagnostics/` (backed by Redis, fed by the firmware's
 * mqtt_heartbeat_worker every ~30s). Polls every 10s since the backing
 * data only refreshes at 30s. A 404 means the supervisor hasn't published
 * yet -- we show a waiting state rather than treating it as an error.
 */
export default function DiagnosticsPanel({ pduiId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await axiosInstance.get(`/pdui/${pduiId}/diagnostics/`);
        if (!cancelled) {
          setData(resp.data);
          setError(null);
          setWaiting(false);
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setWaiting(true);
          setError(null);
        } else {
          setError(e?.response?.data?.error || e.message || "unknown error");
        }
      }
    }
    load();
    const id = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pduiId]);

  const workers = data?.workers || [];
  const snapshots = data?.snapshots || {};
  const systemState = data?.system_state || {};

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: "min(560px, 100vw)",
        height: "100vh",
        overflowY: "auto",
        background: "#111",
        color: "#eee",
        fontFamily: "monospace",
        fontSize: 12,
        padding: 16,
        zIndex: 9999,
        boxShadow: "-2px 0 6px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <strong>Diagnostics (advanced) — pdui {pduiId}</strong>
        <button
          onClick={onClose}
          style={{ background: "#333", color: "#eee", border: "1px solid #555", padding: "2px 8px" }}
        >
          close
        </button>
      </div>

      {error && <div style={{ color: "#f66", marginBottom: 8 }}>Error: {String(error)}</div>}
      {waiting && (
        <div style={{ color: "#fc6", marginBottom: 8 }}>
          Waiting for first diagnostics publish from the device…
        </div>
      )}

      <TelemetryConfigSection pduiId={pduiId} />

      <h4 style={{ margin: "12px 0 4px" }}>Workers</h4>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid #333" }}>
            <th>name</th><th>alive</th><th>period</th><th>age</th><th>fails</th><th>last_error</th>
          </tr>
        </thead>
        <tbody>
          {workers.map((w) => (
            <tr
              key={w.name}
              style={{ borderBottom: "1px solid #222", color: w.consecutive_failures > 0 ? "#fc6" : "#eee" }}
            >
              <td>{w.name}</td>
              <td>{w.alive ? "yes" : "NO"}</td>
              <td>{w.target_period_s}s</td>
              <td>{w.last_heartbeat_age_s ?? "-"}s</td>
              <td>{w.consecutive_failures}/{w.total_failures}</td>
              <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {w.last_error || ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 style={{ margin: "12px 0 4px" }}>Snapshots</h4>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid #333" }}>
            <th>key</th><th>worker</th><th>age</th><th>stale</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(snapshots).map(([key, s]) => (
            <tr key={key} style={{ borderBottom: "1px solid #222", color: s.stale ? "#f66" : "#eee" }}>
              <td>{key}</td>
              <td>{s.worker}</td>
              <td>{s.age_s}s</td>
              <td>{s.stale ? "STALE" : "ok"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 style={{ margin: "12px 0 4px" }}>System state</h4>
      <pre style={{ background: "#000", padding: 8, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(systemState, null, 2)}
      </pre>
    </div>
  );
}
